const ppaCalcTypes = ['MarketShare', 'Elasticity'] as const
export type PPACalcType = (typeof ppaCalcTypes)[number]

/** Asserts whether the given value is a valid PPACalcType */
export function isPPACalcType(value: unknown): value is PPACalcType {
    if (typeof value !== 'string') return false
    return ppaCalcTypes.includes(value as PPACalcType)
}

export function isPPACalcRequest(req: unknown): req is PPACalcRequest {
    const castVal = req as PPACalcRequest
    return isPPACalcType(castVal.Type) && castVal.WbPPAScenarioId !== undefined
}

export function isPPACalcResponse(resp: unknown): resp is PPACalcResponse {
    const castVal = resp as PPACalcResponse
    return (
        typeof castVal.bSuccess === 'boolean' &&
        Array.isArray(castVal.sErrors) &&
        Array.isArray(castVal.sWarnings) &&
        Array.isArray(castVal.sInfo)
    )
}

/** The request that is sent to the PPA modeling engine to calculate market shares or elasticities  */
export type PPACalcRequest = {
    readonly Type: PPACalcType
    readonly WbPPAScenarioId: number
}

/** The response from the PPA modeling engine after calculating market shares or elasticities */
export type PPACalcResponse = {
    readonly bSuccess: boolean
    readonly sErrors: string[]
    readonly sWarnings: string[]
    readonly sInfo: string[]
}

/** Provides methods that call calculations for a PPA scenario. This is the class that should be called by the UI to initiate a calculation  */
export type IPPACalcService = {
    /** Calculates the market shares and writes the results to the database so that the caller can load them when ready */
    CalculateMarketShares(req: PPACalcRequest): Promise<PPACalcResponse>
    /** Calculates the elasticities and writes the results to the database so that the caller can load them when ready */
    CalculateElasticities(req: PPACalcRequest): Promise<PPACalcResponse>
}

/** Provides a method that will execute the PPA model code.
 * This is the class that should be called by the server to initiate a calculation.
 * We're defining it in common to allow more easily sharing the code between the server and the client,
 * and for the client to be able to run the same calculations as the server in the future
 * */
export type IPPACalcExecutor = {
    processRequest(req: PPACalcRequest): Promise<PPACalcResponse>
}
